/*
 * Copyright 2026, Corning Optical Communications
 * All Rights Reserved.
 */

import { CircularProgress } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Icon } from "../../../components/Shared/Icon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { constStr } from "../../../globals/constants";
import { useDeleteReportMutation } from "../../../store/api/devices/deviceApiSlice";
import { IReportsResponse } from "../../../store/api/devices/types";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { errorMessage } from "../../../utils/helpers";

interface IRowData {
  rowData: IReportsResponse;
}

const DeleteReport: React.FC<IRowData> = ({ rowData }) => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [deleteReport] = useDeleteReportMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canDelete = Boolean(permissions?.hasOpCodeGroup("task_management"));

  const openDeleteConfirmation = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    setConfirmOpen(true);
  };

  const confirmDeleteHandler = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setIsDeleting(true);
      await deleteReport({ id: rowData.id }).unwrap();
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Report "${rowData.reportName}" deleted successfully`,
        })
      );
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
    }
  };

  if (!canDelete) {
    return null;
  }

  return !isDeleting ? (
    <>
      <div className="flex items-center">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 bg-transparent text-foreground hover:bg-accent hover:text-foreground"
          aria-label="Delete"
          title="Delete"
          onClick={openDeleteConfirmation}
        >
          <Icon name="delete" size={20} />
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <AlertDialogTitle className="sr-only">Delete Report</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-foreground text-base font-medium break-all overflow-hidden">
              Are you sure you want to delete <strong>{`${rowData.reportName}` || "Unknown"}</strong>?
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setConfirmOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteHandler}>{constStr.yesDelete}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  ) : (
    <CircularProgress size={20} color="primary" />
  );
};

export default DeleteReport;
